<script setup>
import { ref, computed } from "vue";
import axios from "axios";
import BackgroundSection from "./BackgroundSection.vue";
import SendMessageIcon from "./icons/SendMessageIcon.vue";
import content from "../content.js";

const contact = content.contact;

// Parse phone numbers - split by "/" and trim
const phoneNumbers = computed(() => {
    return contact.info.phone
        .split("/")
        .map((phone) => phone.trim())
        .filter((phone) => phone.length > 0);
});

// Form state
const form = ref({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
});

const loading = ref(false);
const success = ref(false);
const error = ref(null);
const errors = ref({});

// Detect if mobile device
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );
};

// Open Google Maps
const openMap = () => {
    const address = encodeURIComponent(contact.info.location);
    const url = isMobile()
        ? `geo:0,0?q=${address}`
        : `https://www.google.com/maps/search/?api=1&query=${address}`;

    window.open(url, isMobile() ? "_self" : "_blank");
};

// Open WhatsApp with a specific phone number
const openWhatsApp = (phoneNumber) => {
    // Remove all non-numeric characters from phone
    const phone = phoneNumber.replace(/\D/g, "");
    const message = encodeURIComponent(
        "Hello, I would like to enquire about your services."
    );
    const url = `https://wa.me/${phone}?text=${message}`;

    window.open(url, isMobile() ? "_self" : "_blank");
};

// Handle form submission
const submitForm = async () => {
    // Reset states
    loading.value = true;
    success.value = false;
    error.value = null;
    errors.value = {};

    try {
        const response = await axios.post("/api/contact/submit", form.value);

        if (response.data.success) {
            success.value = true;
            // Reset form
            form.value = {
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            };

            // Hide success message after 5 seconds
            setTimeout(() => {
                success.value = false;
            }, 5000);
        }
    } catch (err) {
        if (err.response && err.response.status === 422) {
            // Validation errors
            errors.value = err.response.data.errors || {};
            error.value = "Please check the form for errors.";
        } else {
            error.value =
                err.response?.data?.message ||
                "An error occurred. Please try again.";
        }
    } finally {
        loading.value = false;
    }
};

// Get field error
const getError = (field) => {
    return errors.value[field] ? errors.value[field][0] : null;
};
</script>

<template>
    <BackgroundSection
        section-id="contact"
        :video="contact.video"
        :image="contact.image"
        overlay-class="bg-black/60"
    >
        <div
            class="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
        >
            <!-- Contact Info -->
            <div
                class="space-y-6 text-center md:text-left"
                data-aos="fade-right"
            >
                <h2
                    class="text-3xl sm:text-4xl md:text-5xl font-bold text-shadow"
                >
                    {{ contact.title }}
                </h2>
                <p
                    class="text-base sm:text-lg md:text-xl text-gray-100 text-shadow"
                >
                    {{ contact.subtitle }}
                </p>
                <div class="space-y-4 text-lg">
                    <!-- Clickable Address -->
                    <p
                        @click="openMap"
                        class="flex items-center gap-3 text-black cursor-pointer hover:text-indigo-600 transition-colors"
                        title="Click to open in Google Maps"
                    >
                        <span>📍</span>
                        <span class="underline">{{
                            contact.info.location
                        }}</span>
                    </p>

                    <!-- Email -->
                    <p class="flex items-center gap-3 text-black">
                        <span>📧</span>
                        <a
                            :href="`mailto:${contact.info.email}`"
                            class="hover:text-indigo-600 transition-colors underline"
                        >
                            {{ contact.info.email }}
                        </a>
                    </p>

                    <!-- WhatsApp Phone(s) -->
                    <div class="flex items-center gap-3 text-black">
                        <span>☎️</span>
                        <span class="flex flex-wrap gap-x-2">
                            <template
                                v-for="(phone, index) in phoneNumbers"
                                :key="index"
                            >
                                <span
                                    @click="openWhatsApp(phone)"
                                    class="underline cursor-pointer hover:text-indigo-600 transition-colors"
                                    title="Click to chat on WhatsApp"
                                    >{{ phone }}</span
                                >
                                <span
                                    v-if="index < phoneNumbers.length - 1"
                                    class="text-black"
                                    >/</span
                                >
                            </template>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Contact Form -->
            <form
                @submit.prevent="submitForm"
                class="bg-white/70 backdrop-blur-xl rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.18)] p-8 md:p-10 space-y-6 text-gray-900 border border-white/30"
                data-aos="fade-left"
                data-aos-delay="200"
            >
                <!-- Success Message -->
                <div
                    v-if="success"
                    class="bg-green-200 backdrop-blur-md border border-green-400/40 text-green-800 px-4 py-3 rounded-xl"
                    role="alert"
                >
                    <strong class="font-bold">Success!</strong>
                    <span class="block sm:inline">
                        Your message has been sent. We'll get back to you
                        soon.</span
                    >
                </div>

                <!-- Error Message -->
                <div
                    v-if="error"
                    class="bg-red-200 backdrop-blur-md border border-red-400/40 text-red-800 px-4 py-3 rounded-xl"
                    role="alert"
                >
                    <strong class="font-bold">Error!</strong>
                    <span class="block sm:inline"> {{ error }}</span>
                </div>

                <!-- Name -->
                <div>
                    <label
                        class="block text-sm font-medium text-gray-900 mb-1 tracking-wide"
                        >Name <span class="text-red-500">*</span></label
                    >
                    <input
                        v-model="form.name"
                        type="text"
                        class="w-full rounded-xl border border-gray-300/60 bg-white/60 backdrop-blur-sm px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/60 focus:border-green-500 transition"
                        :class="{ 'border-red-500': getError('name') }"
                        placeholder="Your Name"
                        required
                    />
                    <p
                        v-if="getError('name')"
                        class="text-red-500 text-sm mt-1"
                    >
                        {{ getError("name") }}
                    </p>
                </div>

                <!-- Email -->
                <div>
                    <label
                        class="block text-sm font-medium text-gray-900 mb-1 tracking-wide"
                        >Email <span class="text-red-500">*</span></label
                    >
                    <input
                        v-model="form.email"
                        type="email"
                        class="w-full rounded-xl border border-gray-300/60 bg-white/60 backdrop-blur-sm px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/60 focus:border-green-500 transition"
                        :class="{ 'border-red-500': getError('email') }"
                        placeholder="your@email.com"
                        required
                    />
                    <p
                        v-if="getError('email')"
                        class="text-red-500 text-sm mt-1"
                    >
                        {{ getError("email") }}
                    </p>
                </div>

                <!-- Phone -->
                <div>
                    <label
                        class="block text-sm font-medium text-gray-900 mb-1 tracking-wide"
                        >Phone</label
                    >
                    <input
                        v-model="form.phone"
                        type="tel"
                        class="w-full rounded-xl border border-gray-300/60 bg-white/60 backdrop-blur-sm px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/60 focus:border-green-500 transition"
                        :class="{ 'border-red-500': getError('phone') }"
                        placeholder="+60123456789"
                    />
                    <p
                        v-if="getError('phone')"
                        class="text-red-500 text-sm mt-1"
                    >
                        {{ getError("phone") }}
                    </p>
                </div>

                <!-- Subject -->
                <div>
                    <label
                        class="block text-sm font-medium text-gray-900 mb-1 tracking-wide"
                        >Subject</label
                    >
                    <input
                        v-model="form.subject"
                        type="text"
                        class="w-full rounded-xl border border-gray-300/60 bg-white/60 backdrop-blur-sm px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/60 focus:border-green-500 transition"
                        :class="{ 'border-red-500': getError('subject') }"
                        placeholder="Your enquiry"
                    />
                    <p
                        v-if="getError('subject')"
                        class="text-red-500 text-sm mt-1"
                    >
                        {{ getError("subject") }}
                    </p>
                </div>

                <!-- Message -->
                <div>
                    <label
                        class="block text-sm font-medium text-gray-900 mb-1 tracking-wide"
                        >Message <span class="text-red-500">*</span></label
                    >
                    <textarea
                        v-model="form.message"
                        rows="4"
                        class="w-full rounded-xl border border-gray-300/60 bg-white/60 backdrop-blur-sm px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/60 focus:border-green-500 transition"
                        :class="{ 'border-red-500': getError('message') }"
                        placeholder="Tell us more..."
                        required
                    ></textarea>
                    <p
                        v-if="getError('message')"
                        class="text-red-500 text-sm mt-1"
                    >
                        {{ getError("message") }}
                    </p>
                </div>

                <!-- Submit Button -->
                <button
                    type="submit"
                    :disabled="loading"
                    class="group w-full bg-green-600/80 backdrop-blur-md border border-green-500/30 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    <span v-if="loading">Sending... ⏳</span>
                    <span class="flex items-center" v-else
                        >Send Message&nbsp;
                        <span class="inline-block group-hover:animate-shake">
                            <SendMessageIcon />
                        </span>
                    </span>
                </button>
            </form>
        </div>
    </BackgroundSection>
</template>

<style scoped>
@keyframes shake {
    0%,
    100% {
        transform: translateX(0) rotate(0deg);
    }
    10% {
        transform: translateX(-2px) rotate(-5deg);
    }
    20% {
        transform: translateX(2px) rotate(5deg);
    }
    30% {
        transform: translateX(-2px) rotate(-5deg);
    }
    40% {
        transform: translateX(2px) rotate(5deg);
    }
    50% {
        transform: translateX(-2px) rotate(-5deg);
    }
    60% {
        transform: translateX(2px) rotate(5deg);
    }
    70% {
        transform: translateX(-2px) rotate(-5deg);
    }
    80% {
        transform: translateX(2px) rotate(5deg);
    }
    90% {
        transform: translateX(-2px) rotate(-5deg);
    }
}

.group:hover .group-hover\:animate-shake {
    animation: shake 0.6s ease-in-out;
}
</style>
